import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import {
  DEFAULT_LISTING_SORT,
  LISTING_SORT_OPTIONS,
  ListingSortValue,
} from '../utils/listingSort';

/** Canonical page-size options (Users listing / theming). */
export const LISTING_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

export interface ListingPaginationProps {
  page: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeSelectSx?: SxProps<Theme>;
  pageSizeMenuProps?: object;
  /** Hide control when there is only one page. Default true (Users page). */
  hideWhenSinglePage?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Shared listing footer pagination — matches Users page UI for theming consistency.
 * Layout: “Showing X–Y of Z” on the left, numbered MUI Pagination (first/last) on the right.
 */
export const ListingPagination: React.FC<ListingPaginationProps> = ({
  page,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeSelectSx,
  pageSizeMenuProps,
  hideWhenSinglePage = true,
  sx,
}) => {
  if (totalItems <= 0) return null;
  if (hideWhenSinglePage && totalPages <= 1 && !onPageSizeChange) return null;

  const startIndex = (page - 1) * pageSize;
  const endDisplay = Math.min(startIndex + pageSize, totalItems);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 2,
        py: 1.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        ...((sx as object) || {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
          Showing {startIndex + 1}–{endDisplay} of {totalItems}
        </Typography>
        {onPageSizeChange && (
          <ListingPerPageSelect
            value={pageSize}
            onChange={onPageSizeChange}
            sx={pageSizeSelectSx}
            menuProps={pageSizeMenuProps}
          />
        )}
      </Box>
      {totalPages > 1 && (
        <Stack spacing={1} alignItems="center">
          <Pagination
            count={Math.max(totalPages, 1)}
            page={page}
            onChange={(_, v) => onPageChange(v)}
            color="primary"
            size="small"
            showFirstButton
            showLastButton
            sx={{ '& .MuiPaginationItem-root': { fontSize: '0.78rem' } }}
          />
        </Stack>
      )}
    </Box>
  );
};

export interface ListingPerPageSelectProps {
  value: number;
  onChange: (value: number) => void;
  /** Optional FormControl/Select sx merge */
  sx?: SxProps<Theme>;
  menuProps?: object;
}

/** Shared “Per Page” control — same label/options as Users listing. */
export const ListingPerPageSelect: React.FC<ListingPerPageSelectProps> = ({
  value,
  onChange,
  sx,
  menuProps,
}) => (
  <FormControl size="small" sx={{ minWidth: 100, ...(sx as object) }}>
    <InputLabel>Per Page</InputLabel>
    <Select
      value={value}
      label="Per Page"
      onChange={(e) => onChange(Number(e.target.value))}
      MenuProps={menuProps}
    >
      {LISTING_PAGE_SIZE_OPTIONS.map((n) => (
        <MenuItem key={n} value={n}>
          {n}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

export interface ListingSortSelectProps {
  value: ListingSortValue;
  onChange: (value: ListingSortValue) => void;
  sx?: SxProps<Theme>;
  menuProps?: object;
}

/** Shared listing sort control — default is Newest first. */
export const ListingSortSelect: React.FC<ListingSortSelectProps> = ({
  value = DEFAULT_LISTING_SORT,
  onChange,
  sx,
  menuProps,
}) => (
  <FormControl size="small" sx={{ minWidth: 150, ...(sx as object) }}>
    <InputLabel>Sort</InputLabel>
    <Select
      value={value}
      label="Sort"
      onChange={(e) => onChange(e.target.value as ListingSortValue)}
      MenuProps={menuProps}
    >
      {LISTING_SORT_OPTIONS.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

export default ListingPagination;
